import { useRef, type FormEvent, type MouseEvent } from 'react';
import { Card } from '../../components/Card';
import { clsx } from '../../components/clsx';
import { Button, Pill, TextInput } from '../../components/ui';
import { isConfigured } from '../../config/env';
import { useMusic } from './useMusic';
import { useAudioPlayer } from './useAudioPlayer';

/** "0:07". Deezer previews are 30s, so minutes never exceed one digit. */
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const PROMPT = 'How are you feeling? What artist is speaking to you today?';

export function MusicPanel() {
  const music = useMusic();
  const player = useAudioPlayer(
    music.track?.preview ?? null,
    music.playToken,
    music.degradeToDemo,
  );
  const barRef = useRef<HTMLButtonElement | null>(null);

  const track = music.track;
  const searching = music.status === 'searching';

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!searching) music.submit();
  };

  const onSeek = (e: MouseEvent<HTMLButtonElement>): void => {
    const bar = barRef.current;
    if (bar === null) return;
    const rect = bar.getBoundingClientRect();
    if (rect.width === 0) return;
    player.seekToFraction((e.clientX - rect.left) / rect.width);
  };

  return (
    <Card
      title="Music"
      icon="🎵"
      accent="music"
      testId="music-panel"
      badge={music.interpreted ? <Pill tone="accent">AI</Pill> : undefined}
      footer={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={player.toggle}
            disabled={track === null}
            aria-label={player.playing ? 'Pause' : 'Play'}
            title={player.playing ? 'Pause' : 'Play'}
            className={clsx(
              'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
              track === null
                ? 'cursor-not-allowed bg-panel-raised text-ink-subtle'
                : 'bg-accent text-accent-ink hover:opacity-90',
            )}
          >
            {player.playing ? '❚❚' : '▶'}
          </button>

          <button
            type="button"
            ref={barRef}
            onClick={onSeek}
            disabled={track === null}
            aria-label="Seek"
            className="group relative h-4 min-w-0 flex-1 disabled:cursor-not-allowed"
          >
            <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-panel-raised" />
            <span
              className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-accent-music transition-[width] duration-150"
              style={{ width: `${(player.progress * 100).toFixed(2)}%` }}
            />
          </button>

          <span className="shrink-0 font-mono text-[10px] tabular-nums text-ink-subtle">
            {formatTime(player.currentTime)}/{formatTime(player.duration)}
          </span>
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-col gap-2">
        <form onSubmit={onSubmit} className="flex shrink-0 items-center gap-1.5">
          <TextInput
            value={music.input}
            onChange={(e) => music.setInput(e.target.value)}
            placeholder={PROMPT}
            aria-label={PROMPT}
            data-testid="music-input"
            enterKeyHint="search"
            className="flex-1"
          />
          <Button
            type="submit"
            variant="accent"
            disabled={searching}
            data-testid="music-go"
            aria-label="Find a track"
          >
            {searching ? '…' : 'Play'}
          </Button>
        </form>

        <div
          data-testid="now-playing"
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 text-center"
        >
          <Artwork track={track} searching={searching} />

          {track === null ? (
            <p className="max-w-[30ch] text-[11px] leading-snug text-ink-subtle">
              {searching ? 'Finding something for you…' : 'Type a mood or an artist, then hit Play.'}
            </p>
          ) : (
            <>
              <p className="w-full truncate px-1 text-[13px] font-semibold leading-tight text-ink">
                {track.title}
              </p>
              <p className="w-full truncate px-1 text-[11px] leading-tight text-ink-muted">
                {track.artist}
                {track.album !== null && <span className="text-ink-subtle"> · {track.album}</span>}
              </p>
            </>
          )}
        </div>

        <FootNote
          blocked={player.blocked}
          note={music.note}
          interpreted={music.interpreted}
          resolvedQuery={music.resolvedQuery}
        />

        {/* The element itself is never shown; the footer is the transport. */}
        {track !== null && (
          <audio
            ref={player.bind}
            src={track.preview}
            preload="metadata"
            loop={track.source === 'demo'}
          />
        )}
      </div>
    </Card>
  );
}

function Artwork({
  track,
  searching,
}: {
  track: ReturnType<typeof useMusic>['track'];
  searching: boolean;
}) {
  const base =
    'grid h-[86px] w-[86px] shrink-0 place-items-center overflow-hidden rounded-xl border border-line';

  if (searching) {
    return <div className={clsx(base, 'bg-panel-raised motion-safe:animate-pulse')} />;
  }
  if (track?.artwork != null) {
    return (
      <div className={clsx(base, 'bg-panel-raised')}>
        <img
          src={track.artwork}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }
  // No cover art (or nothing loaded): a designed placeholder, not a blank box.
  return (
    <div className={clsx(base, 'bg-panel-raised text-accent-music')}>
      <span className="text-3xl opacity-50" aria-hidden>
        ♪
      </span>
    </div>
  );
}

/**
 * One reserved line for whatever the user most needs to know: an autoplay
 * block beats a degraded-source note beats what Claude searched for.
 * Reserved rather than conditional so the layout never jumps.
 */
function FootNote({
  blocked,
  note,
  interpreted,
  resolvedQuery,
}: {
  blocked: boolean;
  note: string | null;
  interpreted: boolean;
  resolvedQuery: string | null;
}) {
  const text = blocked
    ? 'Your browser blocked autoplay — press play.'
    : note !== null
      ? note
      : interpreted && resolvedQuery !== null
        ? `Claude searched “${resolvedQuery}”.`
        : isConfigured.spotify
          ? 'Spotify client ID detected; this build streams Deezer previews.'
          : '';

  return (
    <p
      className={clsx(
        'line-clamp-2 min-h-[1.6em] shrink-0 text-center text-[10px] leading-tight',
        blocked ? 'text-warn' : 'text-ink-subtle',
      )}
    >
      {text}
    </p>
  );
}
